import React from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  Colors,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js'
import dragDataPlugin from 'chartjs-plugin-dragdata'

import { attivita_lavorativa } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { hoursToTime } from './utils'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Colors, dragDataPlugin)

// ISO Duration parser function for time values
function parseISODuration(d: string): number {
  const m = d.match(/PT(?:(\d+)H)?(?:(\d+)M)?/)
  if (!m) return 0
  const hrs = parseInt(m[1] || '0', 10)
  const mins = parseInt(m[2] || '0', 10)
  return hrs + mins / 60
}

interface DetailBarChartProps {
  attivita: attivita_lavorativa
  onClose: () => void
  onUpdate?: (attivita: attivita_lavorativa) => void
}

const DetailBarChart: React.FC<DetailBarChartProps> = ({ attivita, onClose, onUpdate }) => {
  const handleUpdate = (index: number, value: number) => {
    if (onUpdate) {
      const updatedAttivita = { ...attivita }

      // Convert hours back to ISO duration format
      // This is a simplified conversion - ideally you would use a proper duration formatting library
      const formatToISODuration = (hours: number) => {
        const totalMinutes = Math.floor(hours * 60)
        const hoursPart = Math.floor(totalMinutes / 60)
        const minutesPart = totalMinutes % 60
        return `PT${hoursPart}H${minutesPart}M`
      }

      // Update the appropriate field based on index
      if (index === 0) {
        updatedAttivita.tempo_cliente = formatToISODuration(value)
      } else if (index === 1) {
        updatedAttivita.tempo_trasferta = formatToISODuration(value)
      } else if (index === 2) {
        updatedAttivita.tempo_ufficio = formatToISODuration(value)
      }
      onUpdate(updatedAttivita)
    }
  }

  const data: ChartData<'bar'> = {
    labels: ['Cliente', 'Trasferta', 'Ufficio'],
    datasets: [
      {
        label: `${attivita.ded_Dis?.startsWith('new_activity_') ? 'Nuova Attività' : attivita.ded_Dis} (${new Date(
          attivita.data || new Date(),
        ).toLocaleDateString()})`,
        data: [
          parseISODuration(attivita.tempo_cliente),
          parseISODuration(attivita.tempo_trasferta),
          parseISODuration(attivita.tempo_ufficio),
        ],
        backgroundColor: ['rgba(54, 162, 235, 0.8)', 'rgba(75, 192, 192, 0.8)', 'rgba(255, 159, 64, 0.8)'],
      },
    ],
  }

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: 10,
        suggestedMin: 0,
      },
    },
    onClick(event, elements, chart) {
      if (elements.length > 0) {
        const { index, datasetIndex } = elements[0]
        const value = chart.data.datasets[datasetIndex].data[index] as number
        if (value === 0) {
          handleUpdate(index, 1)
        }
      } else if (event.x) {
        const index = chart.scales.x.getValueForPixel(event.x)
        if (index !== undefined) {
          const value = chart.data.datasets[0].data[index] as number
          console.log('clicked index ', index, ' value ', value)
          if (value === 0) {
            if (index === 2 || attivita.sede) {
              handleUpdate(index, 1)
            }
          }
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Ore per ${
          attivita.ded_Dis?.startsWith('new_activity_') ? 'Nuova Attività' : (attivita.ded_Dis ?? 'Nuova attivita')
        } (${new Date(attivita.data || new Date()).toLocaleDateString()})`,
      },
      tooltip: {
        callbacks: {
          label(tooltipItem) {
            const value = tooltipItem.raw as number
            return hoursToTime(value)
          },
        },
      },
      dragData: {
        round: 2,
        showTooltip: true,
        onDrag: (e, datasetIndex, index, value) => {
          if (index !== 2 && !attivita.sede) return false
        },
        onDragEnd: (e, datasetIndex, index, value) => {
          handleUpdate(index, value as number)
        },
        magnet: {
          to(value) {
            // Round to nearest 0.25 hour
            if (typeof value === 'number') {
              return Math.round(value * 4) / 4
            }
            return value
          },
        },
      },
    },
  }

  return <Bar data={data} options={options} />
}

export default DetailBarChart
